/**
 * DepartmentRoadmapDialog — per-department roadmap (milestones list).
 *
 * Read-only for viewers, editable for admin/engineer. Backed by
 * /api/departments/{dept_id}/roadmap (GET, public) and
 * /api/admin/departments/{dept_id}/roadmap (PUT, admin/engineer).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getAiProviderArgs } from '../lib/aiProviderPicker';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Map as MapIcon, Plus, Trash2, Loader2, Save, GripVertical, Printer, ChevronDown, ChevronUp, Layers } from 'lucide-react';
import { printHtml } from '../lib/print';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTS = [
  { id: 'planned',     label: 'Planned',     color: '#6B7280' },
  { id: 'in-progress', label: 'In progress', color: '#1F4FBE' },
  { id: 'done',        label: 'Done',        color: '#15803D' },
  { id: 'blocked',     label: 'Blocked',     color: '#DC2626' },
];

function esc(v) {
  return String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export default function DepartmentRoadmapDialog({ open, onClose, deptId, deptLabel, canEdit }) {
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('admin_token') : null;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [milestones, setMilestones] = useState([]);
  const [users, setUsers] = useState([]); // [{name, email}]
  // iter-79: roll-up of every roadmap "under" this department. Set
  // when the rollup endpoint returns children — read-only, rendered
  // as a collapsible section below the editable parent roadmap.
  const [rollupChildren, setRollupChildren] = useState([]);
  const [rollupSummary, setRollupSummary] = useState({ child_count: 0, child_milestone_count: 0 });
  const [rollupOpen, setRollupOpen] = useState(true);

  useEffect(() => {
    if (!open) return;
    // Public users list for the assignee dropdown (no auth required).
    axios.get(`${BACKEND}/api/users-public`).then(({ data }) => setUsers(data || [])).catch(() => setUsers([]));
  }, [open]);

  useEffect(() => {
    if (!open || !adminToken) return;
    setLoading(true);
    // Single rollup call returns BOTH the parent's editable roadmap AND
    // every child roadmap that lives under this department.
    axios.get(`${API}/departments/${deptId}/roadmap-rollup`, { headers: { Authorization: `Bearer ${adminToken}` } })
      .then(({ data }) => {
        const parent = data.parent || {};
        setTitle(parent.title || '');
        setDescription(parent.description || '');
        setMilestones(parent.milestones || []);
        setRollupChildren(data.children || []);
        setRollupSummary(data.summary || { child_count: 0, child_milestone_count: 0 });
      })
      .catch(() => {
        // Fallback to the legacy single-roadmap endpoint so an outage of
        // the rollup never blocks editing the parent.
        axios.get(`${API}/departments/${deptId}/roadmap`, { headers: { Authorization: `Bearer ${adminToken}` } })
          .then(({ data }) => {
            setTitle(data.title || '');
            setDescription(data.description || '');
            setMilestones(data.milestones || []);
            setRollupChildren([]);
            setRollupSummary({ child_count: 0, child_milestone_count: 0 });
          })
          .catch(() => { /* empty roadmap is OK */ });
      })
      .finally(() => setLoading(false));
  }, [open, adminToken, deptId]);

  const addMilestone = () => setMilestones((m) => [...m, { id: '', title: '', description: '', due_date: '', status: 'planned', owner: '', assignee_email: '', order: m.length }]);
  const removeMilestone = (idx) => setMilestones((m) => m.filter((_, i) => i !== idx));
  const updateMilestone = (idx, patch) => setMilestones((m) => m.map((x, i) => (i === idx ? { ...x, ...patch } : x)));
  const moveMilestone = (idx, dir) => setMilestones((m) => {
    const target = idx + dir;
    if (target < 0 || target >= m.length) return m;
    const next = [...m];
    [next[idx], next[target]] = [next[target], next[idx]];
    return next.map((x, i) => ({ ...x, order: i }));
  });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/departments/${deptId}/roadmap`,
        { title, description, milestones },
        { headers: { Authorization: `Bearer ${adminToken}` } },
      );
      setMilestones(data.milestones || []);
      toast.success('Roadmap saved.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const printRoadmap = () => {
    const rows = milestones.map((m, i) => `<tr><td>${i + 1}</td><td>${esc(m.title)}</td><td>${esc(m.owner)}</td><td>${esc(m.due_date)}</td><td>${esc(m.status)}</td><td>${esc(m.description)}</td></tr>`).join('');
    const body = `
      <p class="kicker">DREAMS ROSE HALL · ${esc(deptLabel || deptId).toUpperCase()} · ROADMAP</p>
      <h1>${esc(title || `${deptLabel || deptId} Roadmap`)}</h1>
      ${description ? `<p>${esc(description)}</p>` : ''}
      <table><thead><tr><th>#</th><th>Milestone</th><th>Owner</th><th>Due</th><th>Status</th><th>Description</th></tr></thead><tbody>${rows}</tbody></table>
    `;
    printHtml(body, `${deptLabel || deptId} roadmap`);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-4xl max-h-[92vh] w-[95vw] sm:w-auto overflow-hidden flex flex-col p-3 sm:p-6" data-testid="dept-roadmap-dialog" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <MapIcon className="w-5 h-5 text-emerald-600" />
            Roadmap · {deptLabel || deptId}
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500" style={{ fontFamily: 'Manrope' }}>
            Per-department milestone plan. {canEdit ? 'Admins can add, edit, reorder, and delete milestones.' : 'Read-only view — ask an admin to update milestones.'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-gray-500 text-sm">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading roadmap…
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto py-2 space-y-3">
            <div className="grid grid-cols-1 gap-2">
              <input
                data-testid="roadmap-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Roadmap title (e.g. Spa — 2026 Re-opening)"
                disabled={!canEdit}
                className="w-full px-3 py-2 rounded-lg text-sm font-bold border outline-none"
                style={{ borderColor: '#D6D0C0', fontFamily: 'Manrope', color: '#003DA5' }}
              />
              <textarea
                data-testid="roadmap-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Quick context for the team — goals, scope, KPIs."
                disabled={!canEdit}
                rows={2}
                className="w-full px-3 py-2 rounded-lg text-xs border outline-none resize-none"
                style={{ borderColor: '#D6D0C0', fontFamily: 'Manrope' }}
              />
            </div>

            <div className="space-y-2">
              {milestones.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-6" data-testid="roadmap-empty">
                  No milestones yet.{canEdit && ' Click "+ Add milestone" below to start.'}
                </p>
              )}
              {milestones.map((m, idx) => {
                const statusOpt = STATUS_OPTS.find((s) => s.id === m.status) || STATUS_OPTS[0];
                return (
                  <div
                    key={m.id || idx}
                    data-testid={`roadmap-milestone-${idx}`}
                    className="p-3 rounded-xl"
                    style={{ backgroundColor: 'white', border: `1px solid ${statusOpt.color}40` }}
                  >
                    <div className="flex items-start gap-2">
                      {canEdit && (
                        <div className="flex flex-col items-center gap-0.5 pt-1.5">
                          <button onClick={() => moveMilestone(idx, -1)} disabled={idx === 0} title="Move up" className="p-0.5 rounded hover:bg-slate-100 disabled:opacity-30">
                            <GripVertical className="w-3 h-3 text-gray-500" />
                          </button>
                          <span className="text-[10px] font-bold text-gray-500">{idx + 1}</span>
                          <button onClick={() => moveMilestone(idx, 1)} disabled={idx === milestones.length - 1} title="Move down" className="p-0.5 rounded hover:bg-slate-100 disabled:opacity-30">
                            <GripVertical className="w-3 h-3 text-gray-500" />
                          </button>
                        </div>
                      )}
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                        <input
                          data-testid={`roadmap-ms-title-${idx}`}
                          value={m.title}
                          onChange={(e) => updateMilestone(idx, { title: e.target.value })}
                          placeholder="Milestone title"
                          disabled={!canEdit}
                          className="w-full px-2 py-1.5 rounded text-sm font-bold border outline-none"
                          style={{ borderColor: '#E2DBC8', fontFamily: 'Manrope', color: '#1A2E2E' }}
                        />
                        <select
                          data-testid={`roadmap-ms-owner-${idx}`}
                          value={m.assignee_email || ''}
                          onChange={(e) => {
                            const email = e.target.value;
                            const u = users.find((x) => (x.email || '').toLowerCase() === email.toLowerCase());
                            updateMilestone(idx, {
                              assignee_email: email,
                              owner: u ? (u.name || u.email) : '',
                            });
                          }}
                          disabled={!canEdit}
                          className="w-full px-2 py-1.5 rounded text-xs border outline-none"
                          style={{ borderColor: '#E2DBC8', fontFamily: 'Manrope' }}
                        >
                          <option value="">— Assignee —</option>
                          {users.map((u) => (
                            <option key={u.email} value={u.email}>{u.name || u.email}{u.title ? ` · ${u.title}` : ''}</option>
                          ))}
                        </select>
                        <input
                          data-testid={`roadmap-ms-due-${idx}`}
                          type="date"
                          value={m.due_date || ''}
                          onChange={(e) => updateMilestone(idx, { due_date: e.target.value })}
                          disabled={!canEdit}
                          className="w-full px-2 py-1.5 rounded text-xs border outline-none"
                          style={{ borderColor: '#E2DBC8', fontFamily: 'Manrope' }}
                        />
                        <select
                          data-testid={`roadmap-ms-status-${idx}`}
                          value={m.status}
                          onChange={(e) => updateMilestone(idx, { status: e.target.value })}
                          disabled={!canEdit}
                          className="w-full px-2 py-1.5 rounded text-xs border outline-none"
                          style={{ borderColor: '#E2DBC8', color: statusOpt.color, fontWeight: 700, fontFamily: 'Manrope' }}
                        >
                          {STATUS_OPTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                        </select>
                        <textarea
                          data-testid={`roadmap-ms-desc-${idx}`}
                          value={m.description}
                          onChange={(e) => updateMilestone(idx, { description: e.target.value })}
                          placeholder="What needs to happen, dependencies, definition of done…"
                          disabled={!canEdit}
                          rows={2}
                          className="w-full px-2 py-1.5 rounded text-xs border outline-none resize-none md:col-span-2"
                          style={{ borderColor: '#E2DBC8', fontFamily: 'Manrope', color: '#5C6B6B' }}
                        />
                      </div>
                      {canEdit && (
                        <button
                          data-testid={`roadmap-ms-delete-${idx}`}
                          onClick={() => removeMilestone(idx)}
                          className="p-1.5 rounded hover:bg-red-50"
                          title="Delete milestone"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-red-500" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {canEdit && (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  data-testid="roadmap-add-milestone"
                  onClick={addMilestone}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold"
                  style={{ backgroundColor: '#A3D4D420', color: '#003DA5', fontFamily: 'Manrope' }}
                >
                  <Plus className="w-3.5 h-3.5" /> Add milestone
                </button>
                <button
                  type="button"
                  data-testid="roadmap-ai-draft"
                  onClick={async () => {
                    const notes = window.prompt(`Draft a roadmap for ${deptLabel || deptId} with AI.\n\nAny scope notes / focus areas? (optional)`, '');
                    if (notes === null) return;
                    const providerArgs = await getAiProviderArgs();
                    if (providerArgs === null) return; // user cancelled
                    try {
                      const { data } = await axios.post(`${API}/admin/ai/generate-roadmap`, {
                        dept_label: deptLabel || deptId,
                        dept_id: deptId,
                        horizon_months: 12,
                        scope_notes: notes,
                        milestone_count: 8,
                        ...providerArgs,
                      }, { headers: { Authorization: `Bearer ${adminToken}` } });
                      if (data?.title && !title) setTitle(data.title);
                      if (data?.description && !description) setDescription(data.description);
                      const incoming = (data?.milestones || []).map((m, i) => ({
                        id: crypto?.randomUUID?.() || `ai-${Date.now()}-${i}`,
                        title: m.title || '',
                        description: m.description || '',
                        due_date: m.due_date || '',
                        status: m.status || 'planned',
                        owner: m.owner || '',
                        order: milestones.length + i,
                      }));
                      setMilestones([...milestones, ...incoming]);
                      toast.success(`AI drafted ${incoming.length} milestones. Review and Save.`);
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'AI draft failed.');
                    }
                  }}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold text-white"
                  style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)', fontFamily: 'Manrope', boxShadow: '0 4px 12px rgba(139,92,246,0.35)' }}
                  title="Let AI draft a 12-month roadmap for this department"
                >
                  ✨ Draft with AI
                </button>
              </div>
            )}

            {/* iter-79: rolled-up child roadmaps. Every roadmap saved
                "under" this department (workspace descendants AND, for
                F&B, restaurant.* outlets) appears here as a read-only
                section so the department-head view captures the full
                picture without hunting through sub-pages. Hidden when
                there are no children. */}
            {rollupChildren.length > 0 && (
              <div
                data-testid="roadmap-rollup"
                className="mt-4 rounded-xl overflow-hidden"
                style={{ border: '1px solid #C9D6E6', backgroundColor: '#F4F8FD' }}
              >
                <button
                  type="button"
                  data-testid="roadmap-rollup-toggle"
                  onClick={() => setRollupOpen((v) => !v)}
                  className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-blue-50/40 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #1F4FBE 0%, #003DA5 100%)' }}>
                      <Layers className="w-4 h-4 text-white" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: '#003DA5', fontFamily: 'Manrope' }}>Rolled up from sub-areas</p>
                      <p className="text-sm font-bold truncate" style={{ color: '#0A1A2F', fontFamily: 'Manrope' }}>
                        {rollupSummary.child_count} sub-roadmap{rollupSummary.child_count === 1 ? '' : 's'} · {rollupSummary.child_milestone_count} milestone{rollupSummary.child_milestone_count === 1 ? '' : 's'}
                      </p>
                    </div>
                  </div>
                  {rollupOpen ? <ChevronUp className="w-5 h-5 flex-shrink-0" style={{ color: '#003DA5' }} /> : <ChevronDown className="w-5 h-5 flex-shrink-0" style={{ color: '#003DA5' }} />}
                </button>
                {rollupOpen && (
                  <div className="px-4 pb-4 space-y-3 border-t" style={{ borderColor: '#C9D6E6' }}>
                    {rollupChildren.map((child) => (
                      <div key={child.dept_id} data-testid={`roadmap-rollup-child-${child.dept_id}`} className="rounded-lg p-3" style={{ backgroundColor: 'white', border: '1px solid #D9E3F1' }}>
                        <div className="flex items-baseline justify-between gap-2 mb-2">
                          <p className="text-sm font-bold truncate" style={{ color: '#0A1A2F', fontFamily: 'Manrope' }}>
                            {child.child_label || child.dept_id}
                          </p>
                          <span className="text-[10px] px-2 py-0.5 rounded-full font-bold flex-shrink-0" style={{ backgroundColor: '#EAF2FF', color: '#003DA5', fontFamily: 'Manrope' }}>
                            {child.milestones?.length || 0} ms
                          </span>
                        </div>
                        {child.title && (
                          <p className="text-[11px] italic mb-2" style={{ color: '#4A5D5D', fontFamily: 'Manrope' }}>{child.title}</p>
                        )}
                        {(child.milestones || []).length === 0 ? (
                          <p className="text-[11px]" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>No milestones yet.</p>
                        ) : (
                          <ul className="space-y-1">
                            {(child.milestones || []).map((m, i) => {
                              const statusOpt = STATUS_OPTS.find((s) => s.id === m.status) || STATUS_OPTS[0];
                              return (
                                <li key={m.id || i} className="flex items-center gap-2 text-[11px]" style={{ fontFamily: 'Manrope' }}>
                                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: statusOpt.color }} />
                                  <span className="flex-1 truncate" style={{ color: '#1A2E2E' }}>{m.title || '(untitled)'}</span>
                                  {m.due_date && <span className="text-[10px] flex-shrink-0" style={{ color: '#5C6B6B' }}>{m.due_date}</span>}
                                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ backgroundColor: `${statusOpt.color}20`, color: statusOpt.color }}>
                                    {statusOpt.label}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-200">
          <button type="button" onClick={printRoadmap} disabled={milestones.length === 0} data-testid="roadmap-print" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-white disabled:opacity-50" style={{ backgroundColor: '#003DA5', fontFamily: 'Manrope' }}>
            <Printer className="w-3.5 h-3.5" /> Print
          </button>
          {canEdit && (
            <button
              type="button"
              onClick={save}
              disabled={saving || loading}
              data-testid="roadmap-save"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-50"
              style={{ backgroundColor: '#15803D', fontFamily: 'Manrope' }}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save roadmap
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
